// src/components/tokenPage/risk/VarEsHistoryCard.tsx

import React, { useMemo, useState } from "react";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { DataCard } from "@/components/ui";
import { TimeSeriesChart } from "@/components/charts/base";
import { formatDateLabel } from "@/utils/charts/dataUtils";
import { useTokenPrice } from "@/contexts/token";
import { rollingVaR, rollingES, toReturns } from "@/lib/metrics";

type Range = '90d' | '180d' | '365d' | 'All';
const RANGE_TO_DAYS: Record<Range, number> = { '90d': 90, '180d': 180, '365d': 365, 'All': Infinity };

const VarEsHistoryCard: React.FC = () => {
  const [range, setRange] = useState<Range>('365d');
  const [win, setWin] = useState<30|90|180|365>(30);

  const { token, isLoading } = useTokenPrice();
  const prices = token.prices;

  const { dates, var95, es95 } = useMemo(() => {
    const d = prices.dates;
    const values = prices.values;
    
    if (d.length === 0 || values.length === 0) {
      return { dates: [], var95: [], es95: [] };
    }
    
    const rets = toReturns(values);
    const minPoints = Math.max(20, Math.floor(win * 0.6));
    const v = rollingVaR(rets, win, 0.95, minPoints);
    const e = rollingES(rets, win, 0.95, minPoints);
    
    // Convert to percentage
    const vPct = v.map(val => val !== null ? val * 100 : null);
    const ePct = e.map(val => val !== null ? val * 100 : null);
    
    const maxPoints = RANGE_TO_DAYS[range];
    const start = maxPoints === Infinity ? 0 : Math.max(0, d.length - maxPoints);
    const datesAligned = d.slice(start + 1);
    const varAligned = vPct.slice(start, start + datesAligned.length);
    const esAligned = ePct.slice(start, start + datesAligned.length);
    
    // Find first index with valid data in either series
    const varFirst = varAligned.findIndex(val => val !== null && Number.isFinite(val));
    const esFirst = esAligned.findIndex(val => val !== null && Number.isFinite(val));
    const validIndices = [varFirst, esFirst].filter(idx => idx >= 0);
    const firstValidIdx = validIndices.length > 0 ? Math.min(...validIndices) : -1;
    
    if (firstValidIdx >= 0) {
      return { 
        dates: datesAligned.slice(firstValidIdx), 
        var95: varAligned.slice(firstValidIdx), 
        es95: esAligned.slice(firstValidIdx) 
      };
    }
    return { dates: datesAligned, var95: varAligned, es95: esAligned };
  }, [prices, range, win]);

  // Hide if no data available (after loading)
  const hasData = var95.some(v => v !== null && Number.isFinite(v)) || 
                  es95.some(v => v !== null && Number.isFinite(v));
  if (!isLoading && !hasData) {
    return null;
  }

  return (
    <DataCard
      title="VaR / ES (Historical)"
      subtitle="Rolling risk tail estimates"
      icon="activity"
      iconColor="text-amber-400"
      isLoading={isLoading}
      loadingVariant="chart"
      actions={
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-400">Window</span>
          {[30,90,180,365].map(w => (
            <button 
              key={w} 
              className={`text-xs px-2 py-1 rounded-md ${win===w?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} 
              onClick={() => setWin(w as 30|90|180|365)}
            >
              {w}d
            </button>
          ))}
          <span className="text-[11px] text-slate-500 mx-1">|</span>
          <TimeRangePicker options={['90d','180d','365d','All'] as const} value={range} onChange={setRange} theme="blue" />
        </div>
      }
    >
      <TimeSeriesChart
        dates={dates}
        series={[
          { name: 'VaR 95%', values: var95, color: '#f59e0b', areaStyle: true },
          { name: 'ES 95%', values: es95, color: '#f97316', areaStyle: true },
        ]}
        height={400}
        yAxisFormatter={(v) => `${v.toFixed(1)}%`}
        tooltipFormatter={(params) => {
          const items = params as Array<{ axisValue: string; seriesName: string; value: number; marker: string }>;
          if (!items || items.length === 0) return '';
          const dateStr = items[0]?.axisValue || '';
          const formattedDate = formatDateLabel(dateStr, 'medium');
          let tooltip = `<div style="font-size:11px;font-weight:500;margin-bottom:4px;color:#9ca3af;">${formattedDate}</div>`;
          items.forEach((item) => {
            tooltip += `${item.marker} ${item.seriesName}: <span style="color:#f3f4f6;">${Number(item.value).toFixed(2)}%</span><br/>`;
          });
          return tooltip;
        }}
      />
    </DataCard>
  );
};

export default VarEsHistoryCard;
